// useWorkspaceAttribute — one typed workspace attribute, read + write (contract 22 §4).
//
// Reads through 07's `useWorkspace().getAttribute(key)`, writes through `SaveAttributeFlow`, and
// exists largely so nobody has to remember that 07's getter returns the ROW, not the value —
// legacy call sites disagree about this (`CustomMetrics.vue:151` reads `?.value`,
// `ConditionalMetricCreateModal.vue:136` iterates the row) and the untyped blob hides the mistake.
//
// For the selected workspace only. A write aimed at another workspace (R8, onboarding) is a
// `SaveAttributeFlow.save(..., { workspaceId })` call — that is why the flow is on the barrel too.
import { computed, ref, type ComputedRef, type Ref } from 'vue'

import { SaveAttributeFlow, type SaveAttributeResult } from '../flows/SaveAttributeFlow'
import type { WorkspaceAttributeKey, WorkspaceAttributeRegistry } from '../models/attribute.types'

import { useWorkspace } from '@/features/account'
import { ApiError } from '@/shared/http'

export interface UseWorkspaceAttribute<K extends WorkspaceAttributeKey> {
  /** The attribute's value, or `null` when the row is absent or empty. */
  value: ComputedRef<WorkspaceAttributeRegistry[K] | null>
  /** The row id — what selects update-vs-create. `null` means "no row yet". */
  id: ComputedRef<number | null>
  /** Whether the workspace has a value for this key; drives `NotProvidedBadge`. */
  isSet: ComputedRef<boolean>
  /** Write `next`. Resolves the outcome, or `null` when the write failed — in which case `error`
   *  carries the classified `ApiError` and the pane owns the copy. */
  save: (next: WorkspaceAttributeRegistry[K] | null) => Promise<SaveAttributeResult | null>
  /** Clear the attribute. A key that was never set resolves as a `noop` and sends nothing. */
  remove: () => Promise<SaveAttributeResult | null>
  pending: Ref<boolean>
  error: Ref<ApiError | null>
}

export interface UseWorkspaceAttributeOptions {
  flow?: SaveAttributeFlow
}

export function useWorkspaceAttribute<K extends WorkspaceAttributeKey>(
  key: K,
  options: UseWorkspaceAttributeOptions = {},
): UseWorkspaceAttribute<K> {
  const { getAttribute } = useWorkspace()
  const flow = options.flow ?? new SaveAttributeFlow()

  const pending = ref(false)
  const error = ref<ApiError | null>(null)

  const row = computed(() => getAttribute(key))
  const value = computed(() => (row.value?.value ?? null) as WorkspaceAttributeRegistry[K] | null)
  const id = computed(() => {
    const rowId = row.value?.id
    return typeof rowId === 'number' ? rowId : null
  })

  async function write(
    next: WorkspaceAttributeRegistry[K] | null,
  ): Promise<SaveAttributeResult | null> {
    pending.value = true
    error.value = null
    try {
      return await flow.save(key, next, { attributeId: id.value })
    } catch (cause) {
      error.value = ApiError.isApiError(cause) ? cause : ApiError.fromAxios(cause)
      return null
    } finally {
      pending.value = false
    }
  }

  return {
    value,
    id,
    isSet: computed(() => value.value !== null),
    save: (next) => write(next),
    remove: () => write(null),
    pending,
    error,
  }
}
